import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api.ts';
import { socketService } from '../services/socket.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Event, ChatMessage } from '../types/index.ts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { ArrowLeft, Send, Image as ImageIcon, Smile, Wifi, WifiOff, X, Loader2, Video, Reply, CornerDownRight } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Available emoji reactions
const AVAILABLE_REACTIONS = ["❤️", "😂", "😮", "😢", "😡", "👍", "👎", "🔥", "🎉", "💯"];

export default function EventChatDetail() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState<Event | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [selectedMedia, setSelectedMedia] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (eventId) {
      loadEventData();
      loadMessages();
      
      // Connect to Socket.IO
      socketService.connect();
      socketService.joinEventChat(eventId);
      setIsConnected(true);

      // Listen for new messages
      const unsubMessage = socketService.on('new_message', handleNewMessage);
      
      // Listen for typing indicators
      const unsubTyping = socketService.on('user_typing', handleUserTyping);
      const unsubStopTyping = socketService.on('user_stopped_typing', handleUserStopTyping);
      
      // Listen for reaction updates
      const unsubReaction = socketService.on('reaction_update', handleReactionUpdate);

      return () => {
        socketService.leaveEventChat(eventId);
        unsubMessage();
        unsubTyping();
        unsubStopTyping();
        unsubReaction();
      };
    }
  }, [eventId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadEventData = async () => {
    try {
      const data = await api.getEvent(eventId!);
      setEvent(data);
    } catch (error) {
      toast.error('Failed to load event details');
    }
  };

  const loadMessages = async () => {
    try {
      const data = await api.getChatMessages(eventId!);
      setMessages(data);
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const handleNewMessage = (message: ChatMessage) => {
    setMessages((prev) => {
      const exists = prev.some(m => m.message_id === message.message_id);
      if (exists) return prev;
      return [...prev, message];
    });
  };

  const handleReactionUpdate = (data: { 
    message_id: string; 
    reactions: Record<string, string[]>;
  }) => {
    setMessages((prev) => 
      prev.map(msg => 
        msg.message_id === data.message_id 
          ? { ...msg, reactions: data.reactions }
          : msg
      )
    );
  };

  const handleAddReaction = async (messageId: string, emoji: string) => {
    if (!eventId) return;
    try {
      await socketService.addReaction(messageId, emoji, eventId);
    } catch (error) {
      toast.error('Failed to add reaction');
    }
  };

  const handleReply = (message: ChatMessage) => {
    setReplyingTo(message);
    inputRef.current?.focus();
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleUserTyping = (data: { user_id: string; user_name: string; event_id: string }) => {
    if (data.event_id === eventId && data.user_id !== user?.user_id) {
      setTypingUsers((prev) => {
        if (!prev.includes(data.user_name)) {
          return [...prev, data.user_name];
        }
        return prev;
      });
    }
  };

  const handleUserStopTyping = (data: { user_id: string; event_id: string }) => {
    if (data.event_id === eventId) {
      setTypingUsers((prev) => prev.filter(name => name !== data.user_id));
    }
  };

  const handleTyping = () => {
    if (!eventId) return;
    
    socketService.startTyping(eventId);
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    typingTimeoutRef.current = setTimeout(() => {
      socketService.stopTyping(eventId);
    }, 2000);
  };

  const handleMediaSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/quicktime', 'video/webm'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPEG, PNG, WebP, GIF, MP4, MOV, WebM');
      return;
    }

    const isVideo = file.type.startsWith('video/');
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Maximum ${isVideo ? '50MB for videos' : '10MB for images'}`);
      return;
    }

    setSelectedMedia(file);
    const previewUrl = URL.createObjectURL(file);
    setMediaPreview(previewUrl);
  };

  const clearMediaSelection = () => {
    setSelectedMedia(null);
    if (mediaPreview) {
      URL.revokeObjectURL(mediaPreview);
      setMediaPreview(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadMedia = async (): Promise<{ url: string; media_type: string } | null> => {
    if (!selectedMedia) return null;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedMedia);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/upload/chat-media`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await response.json();
      return { url: data.url, media_type: data.media_type };
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload media');
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !selectedMedia) || !eventId) return;

    try {
      setIsSending(true);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      socketService.stopTyping(eventId);

      let mediaUrl: string | undefined;
      let mediaType: string | undefined;
      
      if (selectedMedia) {
        const uploadResult = await uploadMedia();
        if (uploadResult) {
          mediaUrl = uploadResult.url;
          mediaType = uploadResult.media_type;
        } else if (!newMessage.trim()) {
          return;
        }
      }
      
      // Send via Socket.IO with reply reference
      await socketService.sendMessage(
        eventId, 
        newMessage || '', 
        mediaUrl, 
        mediaType,
        replyingTo?.message_id
      );
      setNewMessage('');
      clearMediaSelection();
      setReplyingTo(null);
    } catch (error: any) {
      try {
        await api.sendChatMessage(eventId, newMessage);
        setNewMessage('');
        clearMediaSelection();
        setReplyingTo(null);
      } catch (restError: any) {
        toast.error(restError.message || 'Failed to send message');
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToMessage = (messageId: string) => {
    const element = document.querySelector(`[data-testid="message-${messageId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('bg-primary/20');
      setTimeout(() => element.classList.remove('bg-primary/20'), 2000);
    }
  };

  if (!event) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 md:h-32 md:w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] md:h-[calc(100vh-8rem)] pb-16 md:pb-8">
      {/* Header - Mobile Optimized */}
      <div className="flex items-center space-x-2 md:space-x-4 mb-3 md:mb-6 px-2 md:px-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/chat')}
          data-testid="back-to-chat-list"
          className="h-8 w-8 md:h-10 md:w-10"
        >
          <ArrowLeft className="h-4 w-4 md:h-5 md:w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-lg md:text-2xl font-heading font-bold truncate">{event.title}</h1>
          <p className="text-xs md:text-sm text-gray-400 truncate">{event.artist}</p>
        </div>
        <div className="flex-shrink-0">
          {isConnected ? (
            <Wifi className="h-4 w-4 text-green-500" />
          ) : (
            <WifiOff className="h-4 w-4 text-red-500" />
          )}
        </div>
      </div>

      {/* Messages Container */}
      <Card className="flex-1 glass border-white/10 overflow-hidden flex flex-col mx-2 md:mx-0">
        <div className="flex-1 overflow-y-auto p-2 md:p-4 space-y-2 md:space-y-4" data-testid="messages-container">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-400">
              <p className="text-sm md:text-base">No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((message) => (
              <MessageBubble
                key={message.message_id}
                message={message}
                isOwnMessage={message.user_id === user?.user_id}
                currentUserId={user?.user_id}
                onAddReaction={handleAddReaction}
                onReply={handleReply}
                onScrollToMessage={scrollToMessage}
              />
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Typing Indicator */}
        {typingUsers.length > 0 && (
          <div className="px-2 md:px-4 py-1 md:py-2 text-xs md:text-sm text-gray-400 animate-pulse">
            {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
          </div>
        )}

        {/* Reply Preview */}
        {replyingTo && (
          <div className="px-2 md:px-4 py-2 border-t border-white/10 bg-white/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <CornerDownRight className="h-4 w-4 text-primary flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-primary font-medium">Replying to {replyingTo.user_name}</p>
                  <p className="text-xs text-gray-400 truncate">{replyingTo.content || '[Media]'}</p>
                </div>
              </div>
              <button
                onClick={cancelReply}
                className="p-1 hover:bg-white/10 rounded-full flex-shrink-0"
                data-testid="cancel-reply-btn"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            </div>
          </div>
        )}

        {/* Media Preview */}
        {mediaPreview && selectedMedia && (
          <div className="px-2 md:px-4 py-2 border-t border-white/10">
            <div className="relative inline-block">
              {selectedMedia.type.startsWith('video/') ? (
                <video 
                  src={mediaPreview} 
                  className="h-16 md:h-20 rounded-lg object-cover"
                  muted
                />
              ) : (
                <img 
                  src={mediaPreview} 
                  alt="Preview" 
                  className="h-16 md:h-20 rounded-lg object-cover"
                />
              )}
              <button
                onClick={clearMediaSelection}
                className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 hover:bg-red-600 transition-colors"
                data-testid="clear-media-btn"
              >
                <X className="h-3 w-3 text-white" />
              </button>
              {selectedMedia.type.startsWith('video/') && (
                <div className="absolute bottom-1 left-1 bg-black/70 rounded px-1 text-xs text-white flex items-center gap-1">
                  <Video className="h-3 w-3" /> Video
                </div>
              )}
            </div>
          </div>
        )}

        {/* Input Area - Mobile Optimized */}
        <div className="p-2 md:p-4 border-t border-white/10">
          <div className="flex items-center space-x-1 md:space-x-2">
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleMediaSelect}
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
              className="hidden"
              data-testid="media-file-input"
            />
            
            <Button 
              variant="ghost" 
              size="icon" 
              className="flex-shrink-0 h-8 w-8 md:h-10 md:w-10"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || isSending}
              data-testid="attach-media-btn"
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 md:h-5 md:w-5 animate-spin" />
              ) : (
                <ImageIcon className="h-4 w-4 md:h-5 md:w-5" />
              )}
            </Button>
            <Input
              ref={inputRef}
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                handleTyping();
              }}
              onKeyPress={handleKeyPress}
              placeholder={replyingTo ? "Type your reply..." : "Type a message..."}
              className="flex-1 glass border-white/10 h-9 md:h-10 text-sm md:text-base"
              disabled={isSending || isUploading}
              data-testid="message-input"
            />
            <Button
              onClick={handleSendMessage}
              disabled={(!newMessage.trim() && !selectedMedia) || isSending || isUploading}
              size="icon"
              className="flex-shrink-0 h-8 w-8 md:h-10 md:w-10 shadow-glow"
              data-testid="send-message-btn"
            >
              {isSending ? (
                <Loader2 className="h-4 w-4 md:h-5 md:w-5 animate-spin" />
              ) : (
                <Send className="h-4 w-4 md:h-5 md:w-5" />
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function MessageBubble({
  message,
  isOwnMessage,
  currentUserId,
  onAddReaction,
  onReply,
  onScrollToMessage,
}: {
  message: ChatMessage;
  isOwnMessage: boolean;
  currentUserId?: string;
  onAddReaction: (messageId: string, emoji: string) => void;
  onReply: (message: ChatMessage) => void;
  onScrollToMessage: (messageId: string) => void;
}) {
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const reactions = message.reactions || {};
  
  const totalReactions = Object.values(reactions).reduce((sum, users) => sum + users.length, 0);
  
  const hasUserReacted = (emoji: string) => {
    return currentUserId && reactions[emoji]?.includes(currentUserId);
  };

  return (
    <div 
      className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} group`} 
      data-testid={`message-${message.message_id}`}
      onTouchStart={() => setShowActions(true)}
      onTouchEnd={() => setTimeout(() => setShowActions(false), 3000)}
    >
      <div className={`max-w-[85%] md:max-w-[70%] space-y-1`}>
        {!isOwnMessage && (
          <p className="text-sm font-semibold text-primary px-2 md:px-3">{message.user_name}</p>
        )}
        
        {/* Reply Preview - if this message is a reply */}
        {message.reply_preview && (
          <button
            onClick={() => onScrollToMessage(message.reply_preview!.message_id)}
            className={`flex items-center gap-1 px-2 md:px-3 py-1 text-xs rounded-t-lg 
              ${isOwnMessage ? 'bg-primary/20 ml-auto' : 'bg-white/10'} 
              hover:bg-white/20 transition-colors cursor-pointer w-fit max-w-full`}
          >
            <CornerDownRight className="h-3 w-3 flex-shrink-0 text-gray-400" />
            <span className="text-gray-400 truncate">
              <span className="font-medium">{message.reply_preview.user_name}:</span>{' '}
              {message.reply_preview.content || '[Media]'}
            </span>
          </button>
        )}
        
        <div className="relative">
          <div
            className={`px-3 md:px-4 py-2 rounded-2xl ${
              isOwnMessage
                ? 'bg-primary text-white rounded-br-sm'
                : 'glass border-white/10 rounded-bl-sm'
            } ${message.reply_preview ? 'rounded-t-lg' : ''}`}
          >
            {/* Media content */}
            {message.media_url && (
              <div className="mb-2">
                {message.media_type === 'video' ? (
                  <video 
                    src={message.media_url} 
                    controls 
                    className="max-w-full rounded-lg max-h-48 md:max-h-64"
                    data-testid={`video-${message.message_id}`}
                  />
                ) : (
                  <img 
                    src={message.media_url} 
                    alt="Shared media" 
                    className="max-w-full rounded-lg max-h-48 md:max-h-64 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => window.open(message.media_url, '_blank')}
                    data-testid={`image-${message.message_id}`}
                  />
                )}
              </div>
            )}
            {/* Text content */}
            {message.content && (
              <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
            )}
          </div>
          
          {/* Action buttons - show on hover (desktop) or touch (mobile) */}
          <div
            className={`absolute ${isOwnMessage ? '-left-16 md:-left-20' : '-right-16 md:-right-20'} top-1/2 -translate-y-1/2 
              flex items-center gap-1
              ${showActions ? 'opacity-100' : 'opacity-0'} 
              group-hover:opacity-100 transition-opacity duration-200`}
          >
            <button
              onClick={() => onReply(message)}
              className="p-1.5 md:p-2 rounded-full hover:bg-white/10 transition-colors"
              data-testid={`reply-btn-${message.message_id}`}
            >
              <Reply className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400" />
            </button>
            <button
              onClick={() => setShowReactionPicker(!showReactionPicker)}
              className="p-1.5 md:p-2 rounded-full hover:bg-white/10 transition-colors"
              data-testid={`reaction-btn-${message.message_id}`}
            >
              <Smile className="h-3.5 w-3.5 md:h-4 md:w-4 text-gray-400" />
            </button>
          </div>
          
          {/* Reaction picker popup */}
          {showReactionPicker && (
            <div 
              className={`absolute ${isOwnMessage ? 'right-0' : 'left-0'} -top-12 z-50
                bg-gray-900 border border-white/20 rounded-full px-1 md:px-2 py-1 
                flex items-center gap-0.5 md:gap-1 shadow-xl animate-in fade-in zoom-in duration-200`}
            >
              {AVAILABLE_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => {
                    onAddReaction(message.message_id, emoji);
                    setShowReactionPicker(false);
                  }}
                  className={`p-0.5 md:p-1 rounded-full hover:bg-white/20 transition-colors text-base md:text-lg
                    ${hasUserReacted(emoji) ? 'bg-primary/30' : ''}`}
                  data-testid={`emoji-${emoji}-${message.message_id}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Display reactions */}
        {totalReactions > 0 && (
          <div className={`flex flex-wrap gap-1 px-2 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
            {Object.entries(reactions).map(([emoji, users]) => (
              users.length > 0 && (
                <button
                  key={emoji}
                  onClick={() => onAddReaction(message.message_id, emoji)}
                  className={`flex items-center gap-0.5 md:gap-1 px-1.5 md:px-2 py-0.5 rounded-full text-xs
                    ${hasUserReacted(emoji) 
                      ? 'bg-primary/30 border border-primary/50' 
                      : 'bg-white/10 border border-white/20'
                    } hover:bg-white/20 transition-colors`}
                  data-testid={`reaction-display-${emoji}-${message.message_id}`}
                >
                  <span>{emoji}</span>
                  <span className="text-gray-300">{users.length}</span>
                </button>
              )
            ))}
          </div>
        )}
        
        <p className="text-xs text-gray-500 px-2 md:px-3">
          {format(new Date(message.timestamp || message.created_at), 'h:mm a')}
        </p>
      </div>
    </div>
  );
}
